import { useState, useEffect } from "react";
import { Clock, X, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTrialGuard } from "@/hooks/useTrialGuard";

// TODO: Configure Calendly URL for TaskEng
const CALENDLY_URL = "https://calendly.com/taskeng/demo";

/**
 * Trial banner that displays at the top of the app for users on active trials.
 * Shows countdown timer and provides a CTA to schedule a demo.
 */
export function TrialBanner() {
  const { isTrialUser, isTrialExpired, formatTimeRemainingShort, timeRemainingMs } =
    useTrialGuard();
  const [isDismissed, setIsDismissed] = useState(false);
  const [timeDisplay, setTimeDisplay] = useState<string | null>(null);

  // Update time display every minute
  useEffect(() => {
    if (!isTrialUser || isTrialExpired) return;

    const updateTime = () => {
      setTimeDisplay(formatTimeRemainingShort());
    };

    updateTime();
    const interval = setInterval(updateTime, 60000);

    return () => clearInterval(interval);
  }, [isTrialUser, isTrialExpired, formatTimeRemainingShort, timeRemainingMs]);

  // Check session storage for dismissal on mount
  useEffect(() => {
    const dismissed = sessionStorage.getItem("trialBannerDismissed");
    if (dismissed === "true") {
      setIsDismissed(true);
    }
  }, []);

  // Don't render if not a trial user, trial is expired, or banner is dismissed
  if (!isTrialUser || isTrialExpired || isDismissed) {
    return null;
  }

  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem("trialBannerDismissed", "true");
  };

  const handleScheduleDemo = () => {
    window.open(CALENDLY_URL, "_blank");
  };

  return (
    <div className="bg-terracotta/10 border-b border-terracotta/20">
      <div className="max-w-7xl mx-auto px-4 py-2">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-sm">
            <Clock className="h-4 w-4 text-terracotta flex-shrink-0" />
            <span className="text-foreground">
              <span className="font-medium">Trial Access</span>
              {timeDisplay && (
                <span className="text-muted-foreground">
                  {" "}— {timeDisplay} remaining
                </span>
              )}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleScheduleDemo}
              className="bg-terracotta hover:bg-terracotta-hover text-white"
            >
              <Calendar className="h-3.5 w-3.5 mr-1.5" />
              Schedule Demo
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
              aria-label="Dismiss banner"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
